import { loadFontListWhenGameReady } from './text'
import { getDescendantsText, getDescendants } from '@ps/bridge/qc/node/utils'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { EditorDataScript, nodeTypes } from '@ps/bridge/qc/scripts/editor'
import * as langScript from '@ps/bridge/qc/scripts/language'
import * as languageScript from '@ps/bridge/qc/scripts/language'
import {
  LanguageScript,
  generateLanguageConfigTextByNode,
} from '@ps/bridge/qc/scripts/language'
// @ts-ignore
import qc from 'qc'
import { IframeWindow } from '@ps/bridge/iframe/iframe.window.type'
import { specialNodes } from '@ps/bridge/qc/node/config'
import _ from 'lodash'
import filepath from '@ps/bridge/path'
import { getLanguagesFromGamePlay } from '@ps/workbench/language'
// @ts-ignore
import i18n from 'i18n'
import { hexToRgbaArr, isHexColor } from '@com/utils/color'
import { isSequenceFrame, isParticleParent } from '@ps/bridge/qc/base'
import { fixMovieClipData } from '@ps/bridge/qc/scripts/movie-clip'

// 预处理模块，这里的预处理指的是 gameReady 之后的后处理，如加载一些依赖的资源

/**
 * 游戏 gameReady
 * @param pid
 * @param fontList
 */
export async function loadResourceWhenGameReady({
  pid,
  fontList,
}: {
  pid: number
  fontList: string[]
}): Promise<void> {
  await loadFontListWhenGameReady({ pid, fontList })
}

/**
 * 修复文本节点数据
 * @param node
 */
function fixTextNodeLangKey(node: qc.UIText) {
  const conf = editorScript.getScript(node)
  const langConf = langScript.getScript(node)
  // 没有 oldLangKey 的文本节点说明是从 ps 中新增的节点（从 ps 新增的临时节点是有 oldLangKey 属性的）
  if (conf && conf.$data && !conf.$data.oldLangKey && langConf) {
    // 将 langKey 设置正确（将节点 uuid 赋值给 langKey）
    if (node.uuid !== langConf.langKey) {
      // console.log(node, `${node.name} 没有 $data.oldLangKey 的节点，langKey 与 uuid 不一致，需要修复`)
      langScript.updateLangKey(node, node.uuid)
    }
  }
}

/**
 * 修复数据：
 * 1、用 languages.json 的配置覆盖掉文本节点脚本 langConfig 中的配置，
 * 因为 pt 可以修改 languages.json 中的配置，然后再传回 ps 编辑，所以需要将 languages.json 中的配置覆盖掉文本节点脚本 langConfig 中的配置
 * 2、将 langKey 设置正确
 * @param iframeWindow
 * @param qc
 */
export function fixLanguagesWhenGameReady({
  iframeWindow,
  qc,
}: {
  iframeWindow: IframeWindow
  qc: any
}): void {
  console.log('修复文本节点数据')
  const gamePlayNode = iframeWindow.UIRoot.getChild(specialNodes.gamePlay.name)
  const languagesRes = iframeWindow.languagesMgr.getRes()
  const textNodes = getDescendantsText(gamePlayNode)
  const gamePlayEditorScriptConfig = editorScript.getScript(
    gamePlayNode,
  ) as EditorDataScript
  let langList = gamePlayEditorScriptConfig.languageConfig.langList
  const languages = languagesRes.languages

  if (_.isEmpty(languages) || _.isEmpty(langList)) {
    console.log('语言列表为空')
    return
  }
  // 如果在 PT 中删除了语言，那么在 PS 中也要删除
  if (!_.isEqual(langList.sort(), Object.keys(languages).sort())) {
    console.log('语言列表在 PT 中有删减')
    gamePlayEditorScriptConfig.languageConfig.langList = Object.keys(languages)
    langList = gamePlayEditorScriptConfig.languageConfig.langList
    // 如果删除的刚好是默认语言，那么需要重新设置默认语言
    if (
      gamePlayEditorScriptConfig.languageConfig.defaultLang &&
      !langList.includes(gamePlayEditorScriptConfig.languageConfig.defaultLang)
    ) {
      gamePlayEditorScriptConfig.languageConfig.defaultLang = String(
        langList[0],
      )
    }
  }

  let isNeedToReloadLanguages = false

  _.each(textNodes, (node) => {
    let hasFixedLangKey = false
    _.each(langList, (langItem) => {
      const newConfig = languageScript.getScript(node) as LanguageScript
      const langKey = newConfig.langKey
      const v = languages[langItem][`${langKey}_V`]
      const h = languages[langItem][`${langKey}_H`]
      // 从 languages.json 中如果可以找到配置，则更新到节点的 langConfig 中
      // 横竖屏配置相互兜底
      if (v || h) {
        _.set(newConfig.langConfig, `${langItem}.pdef`, v || h)
        _.set(newConfig.langConfig, `${langItem}.ldef`, h || v)

        // 使用 langKey 去获取数据正常，说明 langKey 正确
        // 但是 langKey 与 uuid 对不上，虽然 langKey 与 languages.json 映射关系是正确的，但是需要将 langKey 改成与 uuid 一样，所以需要修复 langKey
        // 修复完生节点后，生成新的 languages.json 并添加到内容中
        if (newConfig.langKey !== node.uuid) {
          console.log(
            '使用 langKey 去获取数据正常，说明 langKey 正确,但是 langKey 与 uuid 对不上，虽然 langKey 与 languages.json 映射关系是正确的，但是需要将 langKey 改成与 uuid 一样，所以需要修复 langKey，修复完生节点后，生成新的 languages.json 并添加到内容中',
          )
          fixTextNodeLangKey(node)
          isNeedToReloadLanguages = true
          hasFixedLangKey = true
        }
      } else {
        // 如果有多个语言，且存在一个节点的 langKey 不正确，在遍历第一个语言的时候会 fix 掉 langKey，
        // 导致遍历下一个语言不能从 languages.json 中获取到数据，因为 langKey 已经变了，而 languages.json 还是旧的
        if (!hasFixedLangKey) {
          console.error(
            `在 languages.json 中找不到 ${langKey}_V 或 ${langKey}_H 的配置`,
          )
        } else {
          console.log(
            `该节点的 langKey 被修改了，所以找不到 ${langKey}_V 或 ${langKey}_H 的配置，这不是异常报错`,
          )
        }
      }
    })
  })

  if (isNeedToReloadLanguages) {
    languagesRes.languages = getLanguagesFromGamePlay()
    iframeWindow.qc_game.phaser.cache.addJSON(
      'languages',
      filepath.LANGUAGES_JSON,
      languagesRes,
    )
  }
}

/**
 * 将 languages.json 文本配置转换成节点的文本配置
 */
function convertTextStyle(languagesRes: i18n.LanguagesConfig) {
  const languages = languagesRes.languages
  _.each(languages, (lang) => {
    _.each(lang, (text) => {
      _.each(text.style, (styleValue, key) => {
        if (_.isString(styleValue) && isHexColor(styleValue)) {
          text.style[key] = hexToRgbaArr(styleValue)
        }
      })
    })
  })
  return {
    newLanguagesRes: languagesRes,
  }
}

/**
 * gameReady 之后将 languages.json 中数据格式不符合要求的转换成符合要求的格式
 */
export function convertLanguagesConfigWhenGameReady({
  languagesRes,
  iframeWindow,
}: {
  languagesRes: i18n.LanguagesConfig
  iframeWindow: IframeWindow
}): void {
  console.log(
    'gameReady 之后将 languages.json 中数据格式不符合要求的转换成符合要求的格式',
  )
  const { newLanguagesRes } = convertTextStyle(languagesRes)
  if (_.get(iframeWindow, 'qc_game.phaser.cache.addJSON')) {
    iframeWindow.qc_game.phaser.cache.addJSON(
      'languages',
      filepath.LANGUAGES_JSON,
      newLanguagesRes,
    )
  }
}

/**
 * 自研素材初始时的ps.Languages没有langConfig,需要用默认语言初始化
 */
export function fixLangConfigForSelfDev({
  iframeWindow,
}: {
  iframeWindow: IframeWindow
}) {
  const gamePlayNode = iframeWindow.UIRoot.getChild(specialNodes.gamePlay.name)
  const textNodes = getDescendantsText(gamePlayNode)
  _.each(textNodes, (node) => {
    const config = languageScript.getScript(node) as LanguageScript
    if (_.isEmpty(config.langConfig)) {
      config.langConfig = {}
      const eds = editorScript.getScript(gamePlayNode) as EditorDataScript
      const lang = eds.languageConfig.defaultLang
      config.langConfig[lang] = {}
      config.langConfig[lang]!['pdef'] = generateLanguageConfigTextByNode(node)
      config.langConfig[lang]!['ldef'] = generateLanguageConfigTextByNode(node)
    }
  })
}

/**
 * 自研素材初始时需要设置group_节点的playsmart.editor.data的$data的nodeType和nodeOrigin
 * 自研素材初始化时序列帧需要设置的MovieClip里的allFramePlaySmart字段
 */
export function fixNodeForSelfDev({
  iframeWindow,
}: {
  iframeWindow: IframeWindow
}) {
  const gamePlayNode = iframeWindow.UIRoot.getChild(specialNodes.gamePlay.name)
  getDescendants(gamePlayNode, (node) => {
    const conf = editorScript.getScript(node) as EditorDataScript
    const nt = conf.$data.nodeType
    if (
      node.children.length > 0 &&
      !isParticleParent(node) &&
      !Object.values(nodeTypes).includes(nt)
    ) {
      conf.$data.nodeOrigin = 2
      conf.$data.nodeType = 'group'
    }
    if (isSequenceFrame(node)) {
      fixMovieClipData(node)
    }
    return false
  })
}
